// src/pages/providers/4_settings/ProviderSettings.js
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  PhoneAuthProvider,
  multiFactor,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  PhoneMultiFactorGenerator,
  updatePassword,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import {
  buildMfaResolver,
  determineMfaType,
  getPhoneHint,
  getTotpHint,
  sendMfaCode,
  buildTotpAssertion,
} from '../../../hooks/auth/mfaUtils';
import { auth, db, storage as fbStorage } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import Layout from '../../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import MfaDialog from '../../../components/auth/MfaDialog';
import SwitchToTotpDialog from '../../../components/auth/SwitchToTotpDialog';
import PasswordRequirements from '../../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../../hooks/auth/usePasswordValidation';
import InfoTooltip from '../../../components/InfoTooltip';
import { XIcon, UploadIcon } from '@heroicons/react/outline';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import * as Alert from '@radix-ui/react-alert-dialog';
import SignatureDialog from '../../../components/SignatureDialog';
import ManageNotificationsDialog from './components/ManageNotificationsDialog';
import ManageOrgConfigDialog from './components/ManageOrgConfigDialog';
import ManageInstrumentsDialog from './components/ManageInstrumentsDialog';
import RatingScalesSummary from './components/RatingScalesSummary';

const SIGNATURE_STYLES = [
  { id: 'style1', fontFamily: 'Dancing Script', displayName: 'Style 1' },
  { id: 'style2', fontFamily: 'Caveat', displayName: 'Style 2' },
  { id: 'style3', fontFamily: 'Kalam', displayName: 'Style 3' }
];

// Helper function to convert File to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });
};

export default function ProviderSettings() {
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Error code mapping for Settings password change (Clinician)
  // CS-001W: Current password incorrect
  // CS-001R: Requires recent login
  // CS-001S: New password same as current (no-op change)
  // CS-001P: Password policy/validation failure
  // CS-001M: New passwords do not match
  // CS-001V: Invalid MFA verification code
  // CS-001G: Generic error
  
  // Split into separate states for each panel
  const [logoError, setLogoError] = useState('');
  const [logoSuccess, setLogoSuccess] = useState('');
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // 'logo' or 'signature'
  const [isDeleting, setIsDeleting] = useState(false);

// Update the delete handler to properly handle signature state
const handleDelete = async () => {
  setIsDeleting(true);
  try {
    const deleteClinicianBranding = httpsCallable(functions, 'deleteClinicianBranding');
    await deleteClinicianBranding({ type: itemToDelete });
    
    // Update local state
    if (itemToDelete === 'logo') {
      setProviderLogo(null);
      setPreviewLogo(null);
      setLogoHasChanges(false);
      setLogoSuccess('Organisation logo removed successfully');
    } else {
      // Reset signature state
      setPreviewSignature(null);
      setSelectedStyle(null);
      // Default back to the style option after deletion
      setSignatureType('style');
    }
  } catch (err) {
    console.error('Error deleting file:', err);
    if (itemToDelete === 'logo') {
      setLogoError('Failed to delete logo. Please try again.');
    } else {
      console.error('Failed to delete signature. Please try again.');
    }
  } finally {
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }
};

  // Split isSaving state into separate states
  const [isSavingLogo, setIsSavingLogo] = useState(false);
  
  // Split hasChanges into separate states
  const [logoHasChanges, setLogoHasChanges] = useState(false);
  
  const { user, claims, isOrgAdmin, isSupportUser } = useAuth();

  // Microsoft SSO users (e.g. SLaM, 3 Borough Adult ADHD & Autism Service)
  // authenticate via Microsoft Entra, which handles its own MFA via Conditional
  // Access policies. They do not have a Firebase email/password credential, so
  // the "Change Password" and "Two-Factor Authentication" panels are irrelevant
  // and should be hidden entirely for these users.
  const isMicrosoftSSOUser = claims?.authMode === 'with_microsoft_sso';

  const [providerLogo, setProviderLogo] = useState(null);
  // const [signature, setSignature] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [signatureText, setSignatureText] = useState(auth.currentUser.displayName || '');
  
  
  const [previewLogo, setPreviewLogo] = useState(null);
  const [previewSignature, setPreviewSignature] = useState(null);

  // Add this near the other state variables
  const [signatureType, setSignatureType] = useState('style'); // 'style' or 'upload'

  // Add state for signature dialog
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  // Add this state variable after the other state declarations
  const [signatureExists, setSignatureExists] = useState(false);

  // ── Notification Preferences state ───────────────────────────────────
  // Visible only to Org Admin and Support users.  The config is fetched
  // once on mount and the local copy is updated optimistically as the
  // user toggles individual settings.
  const canManageNotifications = isOrgAdmin() || isSupportUser();
  const [notifConfig, setNotifConfig] = useState(null);
  const [isLoadingNotifConfig, setIsLoadingNotifConfig] = useState(false);
  const [isSavingNotifConfig, setIsSavingNotifConfig] = useState(false);
  const [notifSaveSuccess, setNotifSaveSuccess] = useState('');
  const [notifSaveError, setNotifSaveError] = useState('');
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);

  // Organisation config state (support contacts, letterhead, branding, instruments)
  const [orgConfig, setOrgConfig] = useState(null);
  const [isLoadingOrgConfig, setIsLoadingOrgConfig] = useState(false);
  const [orgConfigDialogOpen, setOrgConfigDialogOpen] = useState(false);

  // Instruments dialog state
  const [instrumentsDialogOpen, setInstrumentsDialogOpen] = useState(false);
  const [isSavingInstruments, setIsSavingInstruments] = useState(false);

  //Removed unused function

  //Removed unused function

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/tiff'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      return 'File must be a JPG, PNG, WEBP, or TIFF image';
    }
    if (file.size > maxSize) {
      return 'File must be smaller than 5MB';
    }
    return null;
  };

// Modify handleFileUpload to track changes with specific state variables
const handleFileUpload = (file, type) => {
  const error = validateFile(file);
  if (error) {
    if (type === 'logo') {
      setLogoError(error);
    } else {
      console.error('Signature file error:', error);
    }
    return;
  }

  const reader = new FileReader();
  reader.onloadend = () => {
    if (type === 'logo') {
      setPreviewLogo(reader.result);
      setProviderLogo(file);
      setLogoHasChanges(true);
      setLogoError('');
      setLogoSuccess('');
    } else {
      setPreviewSignature(reader.result);
      // Always set signature type to 'upload' when uploading a signature file
      setSignatureType('upload');
    }
  };
  reader.readAsDataURL(file);
};

// Split save handler into two separate functions
const handleSaveLogo = async () => {
  setIsSavingLogo(true);
  setLogoError('');
  setLogoSuccess('');
  
  try {
    // Convert files to base64 strings
    const updates = {};
    
    if (providerLogo) {
      const logoBase64 = await fileToBase64(providerLogo);
      updates.logo = {
        data: logoBase64,
        type: providerLogo.type,
        name: providerLogo.name
      };
    }
    
    // Only proceed if we have updates
    if (Object.keys(updates).length > 0) {
      const updateClinicianBranding = httpsCallable(functions, 'updateClinicianBranding');
      const result = await updateClinicianBranding(updates);
      
      // The backend returns the Storage path; resolve a download URL so the
      // preview img tag can display the newly uploaded logo immediately.
      if (result.data.logoPath) {
        try {
          const logoRef = storageRef(fbStorage, result.data.logoPath);
          const freshUrl = await getDownloadURL(logoRef);
          setPreviewLogo(freshUrl);
        } catch (_) {
          // Fallback: keep the local data-URI preview the user already sees
        }
      }
      
      setLogoHasChanges(false);
      setLogoSuccess('Organisation logo updated successfully');
    } else {
      setLogoError('No changes to save');
    }

  } catch (err) {
    console.error('Error saving logo:', err);
    setLogoError('Failed to save logo. Please try again.');
  } finally {
    setIsSavingLogo(false);
  }
};

// Fetch existing branding assets (logo and signature) from Firebase Storage on mount.
// For the logo, we use the stored Storage path on the clinician doc and call
// getDownloadURL via the client SDK so authenticated users see the preview.
useEffect(() => {
  const fetchExistingImages = async () => {
    try {
      const clinicianDoc = await getDoc(doc(db, 'clinicians', user.uid));
      if (clinicianDoc.exists()) {
        const data = clinicianDoc.data();
        
        // Load the existing org logo preview from Storage if one has been uploaded
        if (data.logoPath) {
          try {
            const logoRef = storageRef(fbStorage, data.logoPath);
            const logoUrl = await getDownloadURL(logoRef);
            setPreviewLogo(logoUrl);
          } catch (logoErr) {
            // File may have been deleted from Storage without clearing Firestore -
            // treat as "no logo" and let the upload box show instead.
            console.warn('Could not load existing logo from Storage:', logoErr);
          }
          setLogoHasChanges(false);
        }
        
        if (data.signaturePath) {
          setSignatureExists(true);
          
          // Load the existing signature image from Storage so the preview
          // renders immediately rather than showing a "signature exists" message.
          try {
            const sigRef = storageRef(fbStorage, data.signaturePath);
            const sigUrl = await getDownloadURL(sigRef);
            setPreviewSignature(sigUrl);
          } catch (sigErr) {
            console.warn('Could not load existing signature from Storage:', sigErr);
          }
          
          if (data.signatureType === 'upload') {
            setSignatureType('upload');
          } else {
            setSignatureType('style');
            
            if (data.signatureStyle) {
              const style = SIGNATURE_STYLES.find(s => s.id === data.signatureStyle);
              if (style) {
                setSelectedStyle(style);
              }
            }
            
            if (data.signatureText) {
              setSignatureText(data.signatureText);
            }
          }
        } else {
          setSignatureExists(false);
        }
      }
    } catch (err) {
      console.error('Error fetching existing images:', err);
    }
  };

  if (user) {
    fetchExistingImages();
  }
}, [user]);

  // ---------------------------------------------------------------------------
  // Detect the user's currently enrolled MFA method on mount.
  // We inspect Firebase Auth's multiFactor(user).enrolledFactors to determine
  // whether they're using SMS or TOTP, and extract the factor UID (needed for
  // unenrollment) and phone number (for display purposes, SMS only).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    try {
      const enrolled = multiFactor(currentUser).enrolledFactors || [];
      if (enrolled.length > 0) {
        const factor = enrolled[0]; // We currently support one factor at a time
        if (factor.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
          setCurrentMfaMethod('totp');
        } else {
          setCurrentMfaMethod('phone');
          // Extract last 4 digits of the phone number for display
          const phone = factor.phoneNumber || '';
          const last4 = phone.replace(/\D/g, '').slice(-4);
          setCurrentMfaPhoneNumber(last4 ? `••••${last4}` : '');
        }
        setCurrentMfaFactorUid(factor.uid);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Settings] Error reading MFA factors:', err);
      }
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch the organisation's notification config when the component mounts,
  // but only if the logged-in user has the right role (Org Admin / Support).
  // The Cloud Function resolves the orgId from the caller's custom claims.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!canManageNotifications) return;

    const fetchNotifConfig = async () => {
      setIsLoadingNotifConfig(true);
      try {
        const getNotificationConfig = httpsCallable(functions, 'getNotificationConfig');
        const result = await getNotificationConfig();
        const config = result.data?.notificationConfig || {};
        setNotifConfig(config);
      } catch (err) {
        console.error('[Settings] Failed to fetch notification config:', err);
        setNotifSaveError('Failed to load notification settings. Please refresh the page.');
      } finally {
        setIsLoadingNotifConfig(false);
      }
    };

    fetchNotifConfig();
  }, [canManageNotifications]);

  // ---------------------------------------------------------------------------
  // Fetch the organisation's editable config sections (support contacts,
  // letterhead, branding) so the summary panel can show current values and
  // the ManageOrgConfigDialog can pre-populate edit forms.
  // ---------------------------------------------------------------------------
  const fetchOrgConfig = useCallback(async () => {
    if (!canManageNotifications) return;
    setIsLoadingOrgConfig(true);
    try {
      const getOrgConfig = httpsCallable(functions, 'getOrgConfig');
      const result = await getOrgConfig();
      setOrgConfig({
        supportContacts: result.data?.supportContacts || {},
        letterhead: result.data?.letterhead || {},
        branding: result.data?.branding || {},
        instruments: result.data?.instruments || null,
        orgName: result.data?.orgName || '',
        availableAssessmentModules: result.data?.availableAssessmentModules || ['adultAdhd'],
      });
    } catch (err) {
      console.error('[Settings] Failed to fetch org config:', err);
    } finally {
      setIsLoadingOrgConfig(false);
    }
  }, [canManageNotifications]);

  useEffect(() => {
    fetchOrgConfig();
  }, [fetchOrgConfig]);

  // ---------------------------------------------------------------------------
  // Handle successful MFA method switch (callback from SwitchToTotpDialog).
  // Updates the local display state so the Settings page immediately reflects
  // the new MFA method without requiring a page reload.
  // ---------------------------------------------------------------------------
  const handleSwitchMfaSuccess = () => {
    setCurrentMfaMethod('totp');
    setCurrentMfaPhoneNumber('');
    // Re-read the factor UID from the freshly enrolled TOTP factor
    try {
      const user = auth.currentUser;
      if (user) {
        const enrolled = multiFactor(user).enrolledFactors || [];
        const totpFactor = enrolled.find(
            (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
        );
        if (totpFactor) setCurrentMfaFactorUid(totpFactor.uid);
      }
    } catch (_) {}
  };

  // ---------------------------------------------------------------------------
  // Notification preferences handlers
  // ---------------------------------------------------------------------------

  /**
   * Returns the number of enabled ("send" !== false) notifications for a
   * given audience group, used in the summary panel on the settings page.
   */
  const countEnabledNotifs = useCallback((groupKey) => {
    const group = notifConfig?.[groupKey] || {};
    return Object.values(group).filter((entry) => entry.send !== false).length;
  }, [notifConfig]);

  /**
   * Called by ManageNotificationsDialog when the user confirms changes.
   * Persists the full config to Firestore via the updateNotificationConfig
   * Cloud Function, then updates local state to keep the summary counts
   * in sync.
   */
  const handleSaveNotifConfig = async (updatedConfig) => {
    setIsSavingNotifConfig(true);
    setNotifSaveError('');
    setNotifSaveSuccess('');

    try {
      const updateNotificationConfig = httpsCallable(functions, 'updateNotificationConfig');
      const result = await updateNotificationConfig({ notificationConfig: updatedConfig });
      const savedConfig = result.data?.notificationConfig || updatedConfig;
      setNotifConfig(savedConfig);
      setNotifSaveSuccess('Notification settings saved successfully.');
    } catch (err) {
      console.error('[Settings] Failed to save notification config:', err);
      const message =
        err?.message || err?.details?.message || 'Failed to save notification settings.';
      setNotifSaveError(message);
      throw err;
    } finally {
      setIsSavingNotifConfig(false);
    }
  };

  /**
   * Save updated instrument config to Firestore via the updateOrgConfig CF.
   * The instruments section stores patient scale toggles and informant
   * instrument selections.  After a successful save, the local orgConfig
   * state is updated to keep the summary panel in sync.
   */
  const handleSaveInstruments = async (updatedInstruments) => {
    setIsSavingInstruments(true);
    try {
      const updateOrgConfig = httpsCallable(functions, 'updateOrgConfig');
      await updateOrgConfig({ section: 'instruments', data: updatedInstruments });

      // Update local state so summary panel reflects changes immediately
      setOrgConfig((prev) => prev ? { ...prev, instruments: updatedInstruments } : prev);
    } catch (err) {
      console.error('[Settings] Failed to save instrument config:', err);
      throw err;
    } finally {
      setIsSavingInstruments(false);
    }
  };

  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);
  // Single error banner; no per-field error states

  // MFA states
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);
  const [verificationId, setVerificationId] = useState(null);
  const [resolver, setResolver] = useState(null);
  const [mfaType, setMfaType] = useState('phone');
  const recaptchaVerifierRef = useRef(null);
  const [mfaSentAt, setMfaSentAt] = useState(0);
  // Dedicated MFA loading to decouple from the form's loading state
  const [mfaBusy, setMfaBusy] = useState(false);

  // ---------------------------------------------------------------------------
  // Two-Factor Authentication display/switch state
  // ---------------------------------------------------------------------------
  // We read the user's enrolled MFA factors to determine their current method
  // (phone or TOTP) and display it in a "Security" section. SMS users can
  // switch to TOTP (authenticator app) via the SwitchToTotpDialog, which
  // handles the full re-auth + enroll + unenroll flow in-session.
  const [currentMfaMethod, setCurrentMfaMethod] = useState(null); // 'phone' | 'totp' | null
  const [currentMfaFactorUid, setCurrentMfaFactorUid] = useState(null);
  const [currentMfaPhoneNumber, setCurrentMfaPhoneNumber] = useState('');
  const [showSwitchMfaDialog, setShowSwitchMfaDialog] = useState(false);

  const handleNewPasswordChange = (e) => {
    const newPassword = e.target.value;
    setNewPassword(newPassword);

    // Validity surface via PasswordRequirements component

    // Update password match status if confirm password exists
    if (confirmPassword) {
      setPasswordMatch(confirmPassword === newPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    setPasswordMatch(newConfirmPassword === newPassword);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setPasswordSuccess('');
    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in first');

      const { errors } = validatePasswordString(newPassword);
      if (errors.length > 0) {
        throw new Error(errors.join(". "));
      }

      // Validate passwords match
      if (newPassword !== confirmPassword) {
        throw new Error('New passwords do not match');
      }

      // Check if new password is same as current password
      if (newPassword === currentPassword) {
        setError('New password must be different from your current password');
        setErrorCode('CS-001S');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordMatch(null);
        return;
      }

      // Create credential
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      try {
        // Attempt to reauthenticate
        await reauthenticateWithCredential(user, credential);

        // If successful (no MFA required), update password
        await updatePassword(user, newPassword);
        handlePasswordUpdateSuccess();

      } catch (err) {
        // Handle MFA required error
        if (err.code === 'auth/multi-factor-auth-required') {
          try {
            const mfaResolver = buildMfaResolver(auth, err);
            const detectedMfaType = determineMfaType(mfaResolver);

            if (detectedMfaType === 'totp') {
              // TOTP path: no reCAPTCHA or SMS needed
              setResolver(mfaResolver);
              setMfaType('totp');
              setIsLoading(false);
              setShowMFAPrompt(true);
            } else {
              // Phone/SMS path: send verification code via reCAPTCHA
              if (!recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current = new RecaptchaVerifier(auth,
                  'recaptcha-container',
                  { size: 'invisible', callback: () => {} }
                );
              }

              const { verificationId: vId } = await sendMfaCode(
                auth, mfaResolver, recaptchaVerifierRef.current
              );

              setVerificationId(vId);
              setResolver(mfaResolver);
              setMfaType('phone');
              setMfaSentAt(Date.now());
              setIsLoading(false);
              setShowMFAPrompt(true);
            }
            return;

          } catch (mfaError) {
            console.error('Error handling MFA:', mfaError);
            throw new Error('Error setting up MFA verification');
          }
        } else if (err.code === 'auth/wrong-password') {
          setError('Current password is incorrect');
          setErrorCode('CS-001W');
          setCurrentPassword(''); // Clear the current password field
        } else {
          throw err;
        }
      }

    } catch (err) {
      console.error('Change Password Error:', err);
      switch (err.code) {
        case 'auth/wrong-password':
          setError('Current password is incorrect');
          setErrorCode('CS-001W');
          setCurrentPassword(''); // Clear the current password field
          break;
        case 'auth/requires-recent-login':
          setError('Please sign in again before changing your password');
          setErrorCode('CS-001R');
          await signOut(auth);
          navigate('/login');
          break;
        default:
          // Map to user-friendly message + short code, similar to sign-in
          const msg = err?.message || 'Error changing password';
          setError(msg);
          if (msg.includes('Password must')) setErrorCode('CS-001P'); // policy/validation
          else if (msg.toLowerCase().includes('do not match')) setErrorCode('CS-001M'); // mismatch
          else setErrorCode('CS-001G'); // generic
      }
    } finally {
      setIsLoading(false);
    }
  };

  // handleVerifyMFA replaced by shared MfaDialog onVerify

  const handlePasswordUpdateSuccess = () => {
    // Clear form
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');

    setPasswordSuccess('Password successfully updated! Please sign in again.');

    // Sign out user after successful password change
    setTimeout(async () => {
      await signOut(auth);
      navigate('/login');
    }, 2000);
  };

  // Add a function to handle signature type tab changes
  //Removed unused function

  // Simplify the cancel handlers to not fetch images
  const handleCancelLogo = () => {
    setProviderLogo(null);
    setPreviewLogo(null);
    setLogoHasChanges(false);
    setLogoError('');
    setLogoSuccess('');
  };

  //Removed unused function

  // Add a function to close the dialog and reset the save completion status
  //Removed unused function

  return (
    <Layout activePage="settings">
      <div className="flex-col gap-4 p-8 flex items-center justify-center">

        <h2 className="text-2xl font-bold text-center">Settings</h2>

        {/* ── Account Settings Section ─────────────────────────────────────── */}
        <div className="w-full max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-700 mb-3 mt-2">Account Settings</h3>
        </div>

        {/* Password Change + MFA panels are hidden for Microsoft SSO users, who
            authenticate via Entra and don't have Firebase email/password or MFA. */}
        {!isMicrosoftSSOUser && (<>
        <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6 w-full max-w-2xl">
          {passwordSuccess && (
            <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
              {passwordSuccess}
            </div>
          )}

          <div className="space-y-8 text-left">
            {/* Password Change Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Change Password</h3>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        setError('');
                      }}
                      required
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                    >
                      <span className="relative h-5 w-5">
                        <EyeIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showCurrentPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                          }`}
                        />
                        <EyeOffIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showCurrentPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                  {/* per-field error removed; consolidated banner is used */}
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <InfoTooltip
                      side="right"
                      content={"Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"}
                    />
                  </div>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      required
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      <span className="relative h-5 w-5">
                        <EyeIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showNewPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                          }`}
                        />
                        <EyeOffIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showNewPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                  <PasswordRequirements value={newPassword} onValidityChange={setIsPasswordValid} className="mt-2" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      required
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      <span className="relative h-5 w-5">
                        <EyeIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showConfirmPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                          }`}
                        />
                        <EyeOffIcon
                          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                            showConfirmPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                  {passwordMatch !== null && (
                    <div className={`mt-1 overflow-hidden transition-all duration-300 ${passwordMatch !== null ? 'max-h-6' : 'max-h-0'}`}>
                      <div className="flex items-start text-sm">
                        <span className={`mr-2 font-bold ${passwordMatch ? 'text-green-600' : 'text-gray-500'}`} aria-hidden>
                          {passwordMatch ? '✔' : '✘'}
                        </span>
                        <span className={`${passwordMatch ? 'text-green-700' : 'text-gray-700'}`}>
                          {passwordMatch ? 'Passwords match' : 'Passwords do not match'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Consolidated error banner below all inputs and above CTA */}
                <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24 mt-2 mb-2' : 'max-h-0 mt-0 mb-0'}`} role="alert">
                  <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                    <div className="flex items-start">
                      <svg className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        {error}{errorCode ? ` (Code: ${errorCode})` : ''}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoading || !isPasswordValid || !passwordMatch}
                    className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-300 disabled:text-gray-500"
                  >
                    {isLoading ? 'Updating...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>            

            <MfaDialog
              open={showMFAPrompt}
              mfaType={mfaType}
              onOpenChange={(open) => {
                setShowMFAPrompt(open);
                if (!open && recaptchaVerifierRef.current) {
                  try { recaptchaVerifierRef.current.clear(); } catch (_) {}
                  recaptchaVerifierRef.current = null;
                }
              }}
              maskedPhone={mfaType === 'phone' ? (() => { const hint = getPhoneHint(resolver); return hint ? `\u2022\u2022\u2022\u2022${(hint.phoneNumber || '').replace(/\D/g, '').slice(-4)}` : ''; })() : ''}
              isLoading={mfaBusy}
              error={error}
              errorCode={errorCode}
              sentAt={mfaSentAt}
              cooldownSeconds={30}
              tooltipSide="right"
              onVerify={async (code) => {
                try {
                  setMfaBusy(true);
                  if (mfaType === 'totp') {
                    // TOTP (authenticator app) verification flow
                    const totpHint = getTotpHint(resolver);
                    const assertion = buildTotpAssertion(totpHint.uid, code);
                    await resolver.resolveSignIn(assertion);
                  } else {
                    // SMS phone verification flow
                    const cred = PhoneAuthProvider.credential(verificationId, code);
                    const assertion = PhoneMultiFactorGenerator.assertion(cred);
                    await resolver.resolveSignIn(assertion);
                  }
                  const user = auth.currentUser;
                  await updatePassword(user, newPassword);
                  handlePasswordUpdateSuccess();
                } catch (_) {
                  setError('Invalid verification code. Please try again.');
                  setErrorCode('CS-001V');
                } finally {
                  setMfaBusy(false);
                }
              }}
              onCancel={() => setShowMFAPrompt(false)}
              onResend={mfaType === 'phone' ? async () => {
                try {
                  setMfaBusy(true);
                  if (!resolver) return { ok: false };
                  // Delegate SMS resend to the shared mfaUtils helper
                  const { verificationId: newVId } = await sendMfaCode(auth, resolver, recaptchaVerifierRef.current);
                  setVerificationId(newVId);
                  setMfaSentAt(Date.now());
                  return { ok: true };
                } catch (_) {
                  return { ok: false };
                } finally {
                  setMfaBusy(false);
                }
              } : undefined}
            />

          </div>
          <div id="recaptcha-container"></div>
        </div>

        {/* Two-Factor Authentication Section */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6 w-full max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Two-Factor Authentication</h3>
          <p className="text-sm text-gray-500 mb-4">
            Your account is protected with two-factor authentication. Below is your current verification method.
          </p>

          {/* Current MFA method display */}
          <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              {/* Icon based on method */}
              {currentMfaMethod === 'totp' ? (
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                </div>
              ) : (
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                  </svg>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {currentMfaMethod === 'totp' ? 'Authenticator App' : 'SMS Text Message'}
                </p>
                <p className="text-xs text-gray-500">
                  {currentMfaMethod === 'totp'
                    ? 'Codes generated by your authenticator app'
                    : `Verification codes sent to ${currentMfaPhoneNumber || 'your phone'}`
                  }
                </p>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          </div>

          {/* Switch MFA method button — only shown for SMS users. TOTP-to-SMS
              switching is not supported in this iteration. */}
          {currentMfaMethod === 'phone' && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowSwitchMfaDialog(true)}
                className="text-sm font-medium text-primary hover:text-primary/80 underline"
              >
                Switch to Authenticator App
              </button>
            </div>
          )}
        </div>

        {/* Switch-to-TOTP dialog — handles the full re-auth + enroll + unenroll
            flow in a multi-step dialog without signing the user out. */}
        <SwitchToTotpDialog
          open={showSwitchMfaDialog}
          onOpenChange={setShowSwitchMfaDialog}
          onSuccess={handleSwitchMfaSuccess}
          currentMfaFactorUid={currentMfaFactorUid}
        />
        </>)}

        {/* Digital Signature Panel */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6 w-full max-w-2xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Digital Signature</h3>
            <button
              onClick={() => setSignatureDialogOpen(true)}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              {signatureExists ? 'Change Signature' : 'Add Signature'}
            </button>
          </div>
          
          <p className="text-sm text-gray-500">
            Your signature will appear in generated documents that you review and approve.
          </p>
          
          {signatureExists && !previewSignature && (
            <div className="mt-4 p-3 bg-blue-50 text-blue-700 rounded-md text-sm">
              You have already uploaded a signature. Click &apos;Change Signature&apos; to modify it.
            </div>
          )}
          
          {previewSignature && (
            <div className="mt-4 border-2 border-primary/30 bg-primary/5 rounded-lg p-4">
              <div className="relative w-full max-w-[200px] mx-auto">
                <img 
                  src={previewSignature} 
                  alt="Signature preview" 
                  className="w-full h-auto rounded-md"
                />
              </div>
            </div>
          )}
        </div>

        {/* Signature Dialog */}
        <SignatureDialog
          open={signatureDialogOpen}
          onOpenChange={setSignatureDialogOpen}
          currentUserName={auth.currentUser.displayName || ''}
          initialSignature={{
            signatureType: signatureType,
            signatureText: signatureText,
            signatureStyle: selectedStyle?.id,
            previewUrl: previewSignature
          }}
          onSave={async (signatureData) => {
            const updates = {};
            
            if (signatureData.data) {
              updates.signature = {
                data: signatureData.data,
                type: signatureData.type,
                name: signatureData.name,
                signatureType: signatureData.signatureType
              };
              
              if (signatureData.style) {
                updates.signature.style = signatureData.style;
                updates.signature.text = signatureData.text;
              }
            }
            
            if (signatureData.signatureMetadata) {
              updates.signatureMetadata = signatureData.signatureMetadata;
            }
            
            const updateClinicianBranding = httpsCallable(functions, 'updateClinicianBranding');
            const result = await updateClinicianBranding(updates);
            
            if (result.data && result.data.signatureUrl) {
              setPreviewSignature(result.data.signatureUrl);
            }
            
            setSignatureExists(true);
            
            if (signatureData.signatureType) {
              setSignatureType(signatureData.signatureType);
            }
            
            if (signatureData.text) {
              setSignatureText(signatureData.text);
            }
            
            return result;
          }}
          onDelete={async (type) => {
            setItemToDelete(type);
            setDeleteDialogOpen(true);
            setSignatureDialogOpen(false);
          }}
        />

        {/* ── Organisation Settings Section ──────────────────────────────── */}
        <div className="w-full max-w-2xl">
          <h3 className="text-lg font-semibold text-gray-700 mb-3 mt-4">Organisation Settings</h3>
        </div>

        {/* Organisation Logo Panel */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6 w-full max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Organisation Logo</h3>

  <div className="mb-2">
            <p className="text-sm text-gray-500">
        Your Organisation logo will appear in generated documents.
      </p>
    </div>
          
  <div className="flex items-start space-x-4">
  <div className="flex-1">
        {!isOrgAdmin() ? (
          // Read-only view for Clinicians
          <div className="border-2 rounded-lg p-4 border-gray-100 bg-gray-50">
              <p className="text-sm text-gray-500 text-center">
                    Organisation logo can only be managed by your Organisation Administrator
              </p>
          </div>
        ) : (
          // Editable view for Org Admins
          previewLogo ? (
            <div className="border-2 border-primary/30 bg-primary/5 rounded-lg p-4">
              <div className="relative w-full max-w-[200px] mx-auto">
                <img 
                  src={previewLogo} 
                  alt="Organisation logo preview" 
                  className="w-full h-auto rounded-md"
                />
                <button
                  onClick={() => {
                    setItemToDelete('logo');
                    setDeleteDialogOpen(true);
                  }}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div 
              className="relative border-2 border-dashed rounded-lg p-4 border-gray-300 hover:border-primary/50 transition-colors duration-200"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add('border-primary/50', 'bg-primary/5');
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add('border-primary/50', 'bg-primary/5');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-primary/50', 'bg-primary/5');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-primary/50', 'bg-primary/5');
                
                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                  handleFileUpload(files[0], 'logo');
                }
              }}
            >
              <div className="flex flex-col items-center justify-center">
                <UploadIcon className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">
                  Drag and drop or click to upload
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PNG, JPG, WEBP, TIFF (max 5MB)
                </p>
              </div>
              <input
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                accept=".jpg,.jpeg,.png,.webp,.tiff"
              />
            </div>
          )
        )}
      </div>
    </div>
          
          {/* Logo panel action buttons */}
          {isOrgAdmin() && (
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCancelLogo}
                disabled={!logoHasChanges || isSavingLogo}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLogo}
                disabled={!logoHasChanges || isSavingLogo}
                className="px-4 py-2 text-sm font-semibold text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                {isSavingLogo ? 'Saving...' : 'Save Logo'}
              </button>
  </div>
          )}
          
          {logoSuccess && (
            <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-md text-sm">
              {logoSuccess}
            </div>
          )}

          {logoError && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {logoError}
            </div>
          )}
        </div>

        {/* ── Organisation Details Panel ────────────────────────────
            Summary of support contacts, letterhead, and branding config
            with a button to open the multi-step edit dialog. */}
        {canManageNotifications && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-1">Organisation Details</h3>
            <p className="text-sm text-gray-500 mb-4">
              Manage your organisation&apos;s support contact details, document letterhead, and email branding.
            </p>

            {isLoadingOrgConfig ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-6 bg-gray-200 rounded-lg w-3/4" />
                ))}
            </div>
            ) : orgConfig ? (
              <div className="space-y-2">
                {/* Support Contacts summary */}
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className={`inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full text-xs font-semibold ${
                    orgConfig.supportContacts?.email ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {orgConfig.supportContacts?.email ? '✓' : '—'}
                  </span>
                  <span className="font-medium">
                    Support Contact Details
                    {orgConfig.supportContacts?.email && (
                      <span className="text-gray-400 ml-1 text-xs">
                        <br />
                        Email: {orgConfig.supportContacts.email}
                        <br />
                        Phone: {orgConfig.supportContacts.phone}
                        <br />
                        Website: {orgConfig.supportContacts.website}
                      </span>
                    )}
                  </span>
                </div>

                {/* Letterhead summary */}
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className={`inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full text-xs font-semibold ${
                    orgConfig.letterhead?.addressLine1 ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {orgConfig.letterhead?.addressLine1 ? '✓' : '—'}
                  </span>
                  <span className="font-medium">
                    Letterhead Address
                    {orgConfig.letterhead?.addressLine1 && (
                      <span className="text-gray-400 ml-1 text-xs">
                        {[
                          orgConfig.letterhead.addressLine1,
                          orgConfig.letterhead.addressLine2,
                          orgConfig.letterhead.addressLine3,
                          orgConfig.letterhead.addressLine4,
                        ].filter(Boolean).map((line, i) => (
                          <span key={i}><br />{line}</span>
                        ))}
                      </span>
                    )}
                  </span>
                </div>

                {/* Branding summary — always shows "configured" since TD
                    defaults (#43213e / #e7c22f) apply to all organisations */}
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    ✓
                  </span>
                  <span className="font-medium">
                    Email Branding
                    <span className="flex items-center gap-1 mr-1">                                         
                      <br />
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: orgConfig.branding?.primaryColor || '#43213e' }}
                      title={orgConfig.branding?.primaryColor || '#43213e'}
                    />
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: orgConfig.branding?.secondaryColor || '#e7c22f' }}
                      title={orgConfig.branding?.secondaryColor || '#e7c22f'}
                    />
                    </span>
                    
                  </span>
              </div>

                <div className="flex justify-end pt-3">
                  <button
                    type="button"
                    onClick={() => setOrgConfigDialogOpen(true)}
                    className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Manage Configuration
                  </button>
            </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                Organisation Details are not available.
              </p>
          )}
        </div>
        )}

        {/* ManageOrgConfigDialog — multi-step dialog for editing org config
            sections (support contacts, letterhead, branding). */}
        {canManageNotifications && orgConfig && (
          <ManageOrgConfigDialog
            isOpen={orgConfigDialogOpen}
            onClose={() => setOrgConfigDialogOpen(false)}
            orgConfig={orgConfig}
            onConfigSaved={fetchOrgConfig}
          />
        )}

        {/* ── Notification Preferences Summary Panel ──────────────────────
            Shows a high-level count of enabled notifications per audience
            group, with a button to open the full management dialog. */}
        {canManageNotifications && (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-1">Notification Preferences</h3>
            <p className="text-sm text-gray-500 mb-4">
              Manage which automated notifications are sent by the system for your organisation.
            </p>

            {notifSaveSuccess && (
              <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
                {notifSaveSuccess}
              </div>
            )}
            {notifSaveError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {notifSaveError}
              </div>
            )}

            {isLoadingNotifConfig ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-6 bg-gray-200 rounded-lg w-3/4" />
                ))}
              </div>
            ) : notifConfig ? (
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    ✓
                  </span>
                  <span className="font-medium">
                    {countEnabledNotifs('patient')} patient emails enabled
                  </span>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    ✓
                  </span>
                  <span className="font-medium">
                    {countEnabledNotifs('informant')} informant emails enabled
                  </span>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    ✓
                  </span>
                  <span className="font-medium">
                    {countEnabledNotifs('providerAlerts')} provider-specific alerts enabled
                  </span>
                </div>

                <div className="flex justify-end pt-3">
                  <button
                    type="button"
                    onClick={() => setNotifDialogOpen(true)}
                    className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Manage Notifications
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">
                Notification settings are not available for your organisation.
              </p>
            )}
          </div>
        )}

        {/* ManageNotificationsDialog — full notification management UI
            rendered as a multi-page dialog with read-only / editing modes
            so that changes require explicit confirmation before saving. */}
        {canManageNotifications && notifConfig && (
          <ManageNotificationsDialog
            open={notifDialogOpen}
            onOpenChange={setNotifDialogOpen}
            notifConfig={notifConfig}
            isSaving={isSavingNotifConfig}
            onSave={handleSaveNotifConfig}
            supportEmail={orgConfig?.supportContacts?.email || ''}
            availableModules={orgConfig?.availableAssessmentModules || ['adultAdhd']}
            organisationName={orgConfig?.orgName || ''}
          />
        )}

        {/* Rating Scales card. CYP ADHD is one of the pathway blocks:
            the readable name, plus SNAP-IV 26, which every org assigns. */}
        {canManageNotifications && (
          <RatingScalesSummary
            isLoading={isLoadingOrgConfig}
            instruments={orgConfig?.instruments || null}
            orgName={orgConfig?.orgName || ''}
            availableModules={orgConfig?.availableAssessmentModules || ['adultAdhd']}
            onManage={() => setInstrumentsDialogOpen(true)}
          />
        )}

        {/* ManageInstrumentsDialog — toggle/radio UI for rating scales */}
        {canManageNotifications && orgConfig?.instruments && (
          <ManageInstrumentsDialog
            open={instrumentsDialogOpen}
            onOpenChange={setInstrumentsDialogOpen}
            instrumentConfig={orgConfig.instruments}
            isSaving={isSavingInstruments}
            onSave={handleSaveInstruments}
            orgName={orgConfig?.orgName || ''}
            availableModules={orgConfig?.availableAssessmentModules || ['adultAdhd']}
          />
        )}

      </div>

      <Alert.Root open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
  <Alert.Portal>
    <Alert.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
    <Alert.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[90vw] max-w-[500px] shadow-xl">
      <Alert.Title className="text-xl font-bold mb-4">
        Confirm Deletion
      </Alert.Title>
      <Alert.Description className="mb-6 text-gray-600">
        Are you sure you want to remove this {itemToDelete === 'logo' ? 'organisation logo' : 'signature'}? This action cannot be undone.
      </Alert.Description>
      
      <div className="flex justify-end gap-4">
        <Alert.Cancel asChild>
          <button 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isDeleting}
          >
            Cancel
          </button>
        </Alert.Cancel>
        <Alert.Action asChild>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </Alert.Action>
      </div>
    </Alert.Content>
  </Alert.Portal>
</Alert.Root>
    </Layout>
  );
}